import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import CachedProfileImage from './CachedProfileImage';

interface ProfileImageSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (imageId: string) => void;
  currentImageId?: string;
  unlockedProfiles?: string[]; // 잠금해제된 프로필 ID 배열
}

// 세트별 이미지 ID 배열
const SET_A_IMAGES = Array.from({ length: 30 }, (_, i) => `basic-${i + 1}`);
const SET_B_IMAGES = Array.from({ length: 21 }, (_, i) => `b-${i}`);
const SET_C_IMAGES = Array.from({ length: 21 }, (_, i) => `c-${i}`);

type ImageSet = 'A' | 'B' | 'C';

const ProfileImageSelectModal: React.FC<ProfileImageSelectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentImageId = 'basic-0',
  unlockedProfiles = []
}) => {
  const [selectedImageId, setSelectedImageId] = useState<string>(currentImageId);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [activeSet, setActiveSet] = useState<ImageSet>('A');
  const lastClickTimeRef = useRef<number>(0);
  const lastClickImageIdRef = useRef<string | null>(null);
  
  // 프로필이 잠금해제되었는지 확인하는 함수
  const isProfileUnlocked = (imageId: string): boolean => {
    // 세트 A는 모두 기본 해제 (basic-N 형식만 사용)
    if (imageId.startsWith('basic-')) return true;
    // 세트 B, C는 unlockedProfiles에 있어야 해제
    return unlockedProfiles.includes(imageId);
  };

  useEffect(() => {
    setSelectedImageId(currentImageId);
    
    // 모달이 열릴 때 세트A의 모든 프로필 이미지를 프리로드
    if (isOpen) {
      const { imageCacheService } = require('../services/imageCacheService');
      imageCacheService.preloadImages(SET_A_IMAGES);
    }
  }, [currentImageId, isOpen]);

  // 모달 열릴 때 배경 스크롤 비활성화
  useEffect(() => {
    if (isOpen) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // 모달 닫힐 때 원래 상태로 복원
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const handleImageError = (imageId: string) => {
    setImageErrors(prev => new Set(prev).add(imageId));
  };

  const handleImageClick = (imageId: string) => {
    // 이미지가 로드되지 않은 경우 선택하지 않음
    if (imageErrors.has(imageId)) {
      return;
    }
    if (!isProfileUnlocked(imageId)) {
      return;
    }

    const now = Date.now();
    const lastId = lastClickImageIdRef.current;
    const lastTime = lastClickTimeRef.current;

    // 같은 이미지를 짧은 시간 안에 두 번 클릭하면 "더블 클릭"으로 간주
    if (lastId === imageId && now - lastTime < 350) {
      lastClickImageIdRef.current = null;
      lastClickTimeRef.current = 0;
      onConfirm(imageId);
      onClose();
      return;
    }

    lastClickImageIdRef.current = imageId;
    lastClickTimeRef.current = now;
    setSelectedImageId(imageId);
  };

  const handleConfirm = () => {
    onConfirm(selectedImageId);
    onClose();
  };

  const handleCancel = () => {
    setSelectedImageId(currentImageId);
    onClose();
  };

  if (!isOpen) return null;

  const renderContent = () => {
    let images: string[] = [];
    
    if (activeSet === 'A') {
      images = SET_A_IMAGES;
    } else if (activeSet === 'B') {
      images = SET_B_IMAGES;
    } else if (activeSet === 'C') {
      images = SET_C_IMAGES;
    }
    
    return (
      <ImageGrid>
        {images.map((imageId) => {
          const isSelected = selectedImageId === imageId;
          const hasError = imageErrors.has(imageId);
          const isUnlocked = isProfileUnlocked(imageId);
          const isLocked = !isUnlocked;
          
          return (
            <ImageItem
              key={imageId}
              onClick={() => isUnlocked && handleImageClick(imageId)}
              $isSelected={isSelected}
              $hasError={hasError}
              $isLocked={isLocked}
            >
              {isLocked ? (
                <LockedProfileOverlay>
                  <LockedIcon>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="10" width="14" height="11" rx="2" fill="#6c757d"/>
                      <path d="M8 10V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V10" stroke="#6c757d" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </LockedIcon>
                </LockedProfileOverlay>
              ) : (
                <>
                  <ProfileImageWrapper>
                    <CachedProfileImage
                      imageId={imageId}
                      alt={`프로필 ${imageId}`}
                      onError={() => handleImageError(imageId)}
                      fallbackImageId="basic-0"
                    />
                  </ProfileImageWrapper>
                  {isSelected && !hasError && (
                    <SelectionIndicator>
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)"/>
                        <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </SelectionIndicator>
                  )}
                </>
              )}
            </ImageItem>
          );
        })}
      </ImageGrid>
    );
  };

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>프로필 이미지 선택</ModalTitle>
          <CloseButton onClick={handleCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="var(--color-text-gray)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </CloseButton>
        </ModalHeader>

        <TabContainer>
          <TabButton $isActive={activeSet === 'A'} onClick={() => setActiveSet('A')}>
            기본
          </TabButton>
          <TabButton $isActive={activeSet === 'B'} onClick={() => setActiveSet('B')}>
            동물
          </TabButton>
          <TabButton $isActive={activeSet === 'C'} onClick={() => setActiveSet('C')}>
            캐릭터
          </TabButton>
        </TabContainer>

        {renderContent()}

        <ButtonContainer>
          <CancelButton data-variant="modal-secondary" onClick={handleCancel}>취소</CancelButton>
          <ConfirmButton onClick={handleConfirm}>확인</ConfirmButton>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProfileImageSelectModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10001;
  padding: 80px 20px;
  overflow-y: auto;
  min-height: 100vh;
  
  @media (max-width: 600px) {
    padding: 60px 20px;
  }
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 846px;
  overflow: visible;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  padding: 24px;
  z-index: 10002;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--color-border);
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.$isActive ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-text-gray)'};
  font-size: 16px;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: -1px;

  &:hover {
    color: var(--color-accent-text);
    background: var(--color-accent-soft);
  }

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
  }
  
  /* 다크모드: 선택한 탭의 텍스트를 흰색으로 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$isActive ? '#ffffff' : 'var(--color-text-gray)'};
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  justify-content: center;
  max-width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ImageItem = styled.div<{ $isSelected: boolean; $hasError: boolean; $isLocked?: boolean }>`
  width: 100%;
  max-width: 150px;
  aspect-ratio: 1;
  border-radius: 12px;
  background: ${props => {
    if (props.$isLocked) return 'var(--color-border)';
    if (props.$hasError) return 'var(--color-surface-highlight)';
    return 'transparent';
  }};
  cursor: ${props => (props.$hasError || props.$isLocked) ? 'not-allowed' : 'pointer'};
  position: relative;
  transition: all 0.2s;
  border: ${props => props.$isSelected ? '4px solid var(--color-accent-strong)' : 'none'};
  opacity: ${props => props.$hasError ? 0.3 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  padding: ${props => props.$isSelected ? '0' : '0'};
  margin: 0 auto;

  @media (max-width: 480px) {
    max-width: 120px;
  }

  &:hover {
    transform: ${props => (props.$hasError || props.$isLocked) ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => (props.$hasError || props.$isLocked) ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.15)'};
    background: ${props => {
      if (props.$isLocked) return 'var(--color-border)';
      if (props.$hasError) return 'var(--color-surface-highlight)';
      if (props.$isSelected) return 'var(--color-accent-strong)';
      return 'rgba(var(--color-accent-rgb), 0.1)';
    }};
  }

  &:focus-visible {
    outline: ${props => (props.$hasError || props.$isLocked) ? 'none' : '2px solid black'};
    outline-offset: 2px;
  }
`;

const ProfileImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  border: 2px solid #000000;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LockedProfileOverlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-border);
  border-radius: 8px;
`;

const LockedIcon = styled.div`
  opacity: 0.5;
`;


const SelectionIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  color: var(--color-accent-text);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--color-accent-soft);
  }

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.9);
  }

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
  }
`;

